import { Component, Inject } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { ItemChanges } from '../../../../models/dtos';
import { MaterialCategory } from '../../../../models/dtos/Enums/MaterialCategory';
import { CONSTANTS } from '../../../../Constants/Constants';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';

@Component({
  selector: 'app-material-checker-material-changes-dialog',
  imports: [
    MatDialogClose,
    MatDialogContent,
    MatDialogActions,
    MatTableModule,
    MatButtonModule,
    TranslatePipe,
    MatIconModule,
    MatTooltip,
    CurrencyPipe,
    MatPaginatorModule,
    FormsModule,
    MatSortModule,
    CloseOnEscapeDirective
  ],
  templateUrl: './sol-material-checker-material-changes-dialog.component.html',
  styleUrl: './sol-material-checker-material-changes-dialog.component.scss'
})
export class SolMaterialCheckerMaterialChangesDialogComponent {

  private originalMaterials: ItemChanges[] = [];
  private allMaterials: ItemChanges[] = [];
  private currentSort: Sort = { active: CONSTANTS.EMPTY_STRING, direction: CONSTANTS.EMPTY_STRING as 'asc' | 'desc' | '' };
  dataSource: ItemChanges[] = [];
  totalCount: number = CONSTANTS.ZERO;
  currentPageNumber: number = CONSTANTS.ONE;
  pageSize: number = CONSTANTS.TWENTY;
  gotoPageNumber: number | null = null;

  displayedColumns: string[] = [
    'ssn', 
    'changeType', 
    'isPBOMarked', 
    'oldCost', 
    'newCost', 
    'currentList', 
    'newList',
    'costPlus',
    'anyLaborChange',
    'currentTaskCategoryNo', 
    'newTaskCategory',
    'currentVendorNumber', 
    'newVendorNumber', 
    'currentModelNumber',
    'newModelNumber', 
    'type', 
    'currentIOType', 
    'newIOType'
  ];
  selectedRow: ItemChanges;

  constructor(
    private readonly translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: { itemChanges: ItemChanges[] }
  ) {
    if (data?.itemChanges) {
      this.originalMaterials = data.itemChanges;
      this.applySorting();
    }
  }

  get totalPages(): number {
    return this.pageSize > CONSTANTS.ZERO ? Math.ceil(this.totalCount / this.pageSize) : CONSTANTS.ZERO;
  }

  onPageChange(event: PageEvent): void {
    this.currentPageNumber = event.pageIndex + CONSTANTS.ONE;
    this.pageSize = event.pageSize;
    this.updateDisplayedData();
  }

  onSortChange(sort: Sort): void {
    this.currentSort = sort;
    this.currentPageNumber = CONSTANTS.ONE;
    this.applySorting();
  }

  gotoPage(): void {
    if (this.gotoPageNumber && this.gotoPageNumber >= CONSTANTS.ONE && this.gotoPageNumber <= this.totalPages) {
      this.currentPageNumber = this.gotoPageNumber;
      this.gotoPageNumber = null;
      this.updateDisplayedData();
    }
  }

  private updateDisplayedData(): void {
    const start = (this.currentPageNumber - CONSTANTS.ONE) * this.pageSize;
    this.dataSource = this.allMaterials.slice(start, start + this.pageSize);
  }

  private applySorting(): void {
    const { active, direction } = this.currentSort;

    if (!active || !direction) {
      this.allMaterials = [...this.originalMaterials];
    } else {
      this.allMaterials = [...this.originalMaterials].sort((a, b) => {
        const valueA = this.getSortValue(a, active);
        const valueB = this.getSortValue(b, active);
        const comparison = this.compareValues(valueA, valueB);
        return direction === 'asc' ? comparison : -comparison;
      });
    }

    this.totalCount = this.allMaterials.length;
    this.updateDisplayedData();
  }

  private getSortValue(item: ItemChanges, column: string): string | number {
    if (column === 'anyLaborChange') {
      return this.isAnyLaborChange(item);
    }

    const value = (item as unknown as Record<string, unknown>)[column];

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'boolean') {
      return value ? CONSTANTS.ONE : CONSTANTS.ZERO;
    }

    return String(value ?? CONSTANTS.EMPTY_STRING).toLowerCase();
  }

  private compareValues(a: string | number, b: string | number): number {
    if (a < b) {
      return -CONSTANTS.ONE;
    }

    if (a > b) {
      return CONSTANTS.ONE;
    }

    return CONSTANTS.ZERO;
  }

  onRowClick(row: ItemChanges){
    this.selectedRow = row;
  }

  copyToClipboard(): void {
    const headers = this.getTranslatedHeaders();
    const rows = this.allMaterials.map(row =>
      this.displayedColumns.map(col => (row as any)[col] ?? '').join('\t')
    );
    const tableText = [headers.join('\t'), ...rows].join('\n');
    navigator.clipboard.writeText(tableText)
  }

  
  formatTableData(): string {
    const headers = this.getTranslatedHeaders();
    const escapeCsv = (value: any): string => {
      const str = String(value ?? '');
      return str.includes(',') || str.includes('"') || str.includes('\n') 
        ? `"${str.replace(/"/g, '""')}"` 
        : str;
    };
    const rows = this.allMaterials.map(row =>
      this.displayedColumns.map(col => escapeCsv((row as any)[col])).join(',')
    );
    return [headers.map(escapeCsv).join(','), ...rows].join('\n');
  }
  
  getTranslatedHeaders(): string[] {
    const translationKeyMap: Record<string, string> = {
      'ssn': 'MATERIAL',
      'changeType': 'CHANGE',
      'isPBOMarked': 'PROVIDED_BY_OTHERS',
      'oldCost': 'CURRENT',
      'newCost': 'NEW',
      'currentList': 'CURRENT_LIST',
      'newList': 'NEW_LIST',
      'costPlus': 'COST_PLUS',
      'anyLaborChange': 'ANY_LABOR_CHANGE',
      'currentTaskCategoryNo': 'CURRENT_TASK_CATEGORY',
      'newTaskCategory': 'NEW_TASK_CATEGORY',
      'currentVendorNumber': 'CURRENT_VENDOR_NUMBER',
      'newVendorNumber': 'NEW_VENDOR_NUMBER',
      'currentModelNumber': 'CURRENT_MODEL_NUMBER',
      'newModelNumber': 'NEW_MODEL_NUMBER',
      'type': 'TYPE',
      'currentIOType': 'CURRENT_FIXED_IO',
      'newIOType': 'NEW_FIXED_IO'
    };
    
    return this.displayedColumns.map(col => {
      const translationKey = translationKeyMap[col];
      return translationKey ? this.translateService.instant(translationKey) : col;
    });
  }

  exportToExcel(): void {
    const content = this.formatTableData(); 
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Material_Changes_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  isAnyLaborChange(element: ItemChanges): string {
    const anyLaborChange = element.oldProjectManagementLabor !== element.newProjectManagementLabor
      || element.oldEngineerLabor !== element.newEngineerLabor
      || element.oldSpecialistLabor !== element.newSpecialistLabor
      || element.oldMechanicalEngineerLabor !== element.newMechanicalEngineerLabor
      || element.oldElectricalEngineerLabor !== element.newElectricalEngineerLabor;

    return anyLaborChange ? 'Yes' : 'No';
  }

  getMaterialCategoryName(type: number): string {
    const categoryName = MaterialCategory[type];
    if (!categoryName) {
      return String(type);
    }
    const translationKey = `MATERIAL_CATEGORY_${categoryName.toUpperCase()}`;
    const translatedValue = this.translateService.instant(translationKey);
    // Fall back to the raw enum name if no translation is registered for the current culture
    return translatedValue === translationKey ? categoryName : translatedValue;
  }

  trackByMaterial(index: number, item: ItemChanges): string {
    return item.ssn;
  }

}
